import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Briefcase, Users, Phone, Star, TrendingUp, ArrowRight, Sparkles, Zap, Award, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [callsByDay, setCallsByDay] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('workspace'); // 'personal' | 'team' | 'workspace'
  const [chartHeight, setChartHeight] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768 ? 200 : 280
  );
  const navigate = useNavigate();

  // Existing fetchData — UNCHANGED
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ovRes, cbdRes] = await Promise.all([
          api.get('/analytics/overview'),
          api.get('/analytics/calls-by-day?days=14')
        ]);
        setOverview(ovRes.data);
        setCallsByDay(cbdRes.data || []);
      } catch (err) { logger.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  // Chart height adapts on resize
  useEffect(() => {
    const handleResize = () => {
      setChartHeight(window.innerWidth < 768 ? 200 : window.innerWidth < 1024 ? 240 : 280);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // AI Insights — frontend mock data only (no API)
  const aiInsights = [
    {
      id: 1,
      title: '3 High-Fit Candidates Ready',
      subtitle: '92%+ skills match for Senior Engineer',
      action: 'Screen Now',
      icon: Sparkles,
      bg: 'from-emerald-500 to-emerald-600',
      count: 3,
      route: '/candidates?tab=high-fit',
    },
    {
      id: 2,
      title: 'Auto-Shortlist Waiting',
      subtitle: 'AI flagged 7 exceptional resumes',
      action: 'Review Shortlist',
      icon: Award,
      bg: 'from-blue-500 to-blue-600',
      count: 7,
      route: '/candidates?tab=shortlist',
    },
    {
      id: 3,
      title: 'Optimize Screening Questions',
      subtitle: 'Current set converts 23% → try new variant',
      action: 'Test New Questions',
      icon: Zap,
      bg: 'from-amber-500 to-amber-600',
      count: null,
      route: '/settings?tab=questions',
    },
  ];

  const statCards = [
    {
      label: 'Active Jobs',
      value: viewMode === 'personal' ? 2 : overview?.total_jobs || 0,
      trend: '+3 (12%)',
      icon: Briefcase,
      color: 'text-blue-600 bg-blue-50 border-blue-200',
      route: '/jobs',
    },
    {
      label: 'Total Candidates',
      value: viewMode === 'personal' ? 15 : overview?.total_candidates || 0,
      trend: '+18 (42%)',
      icon: Users,
      color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
      route: '/candidates',
    },
    {
      label: 'Screenings Done',
      value: viewMode === 'personal' ? 8 : overview?.completed_calls || 0,
      trend: '+2 (33%)',
      icon: Phone,
      color: 'text-amber-600 bg-amber-50 border-amber-200',
      route: '/calls',
    },
    {
      label: 'Avg Score',
      value: overview?.avg_resume_score ? `${overview.avg_resume_score}/10` : '8.2',
      trend: '+0.4 (5%)',
      icon: Star,
      color: 'text-violet-600 bg-violet-50 border-violet-200',
      route: '/reports',
    },
  ];

  const quickStatColor = { 'emerald-500': 'text-emerald-700', 'blue-500': 'text-blue-700', 'amber-500': 'text-amber-700', 'violet-500': 'text-violet-700' };

  if (loading) {
    return (
      <div className="p-8 bg-gradient-to-br from-slate-50 to-blue-50/30 min-h-screen" data-testid="dashboard-loading" style={{ fontFamily: 'Georgia, serif' }}>
        {/* Header Skeleton */}
        <div className="mb-10">
          <div className="h-12 w-64 bg-slate-200 rounded-2xl skeleton-shimmer mb-2"></div>
          <div className="h-4 w-48 bg-slate-200/70 rounded-full skeleton-shimmer"></div>
        </div>

        {/* Selector Skeleton */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8 bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-slate-200/30 shadow-sm">
          <div className="h-6 w-20 bg-slate-200 rounded-lg skeleton-shimmer"></div>
          <div className="h-10 w-32 bg-slate-200 rounded-lg skeleton-shimmer flex-1"></div>
          <div className="h-5 w-20 bg-slate-200/70 ml-auto rounded-full skeleton-shimmer"></div>
        </div>

        {/* Stat Cards Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="bg-white/60 backdrop-blur-sm border border-slate-200/40 rounded-2xl p-7">
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 bg-slate-200 rounded-xl skeleton-shimmer"></div>
                <div className="w-5 h-5 bg-slate-200/70 rounded-full skeleton-shimmer"></div>
              </div>
              <div className="h-10 w-24 bg-slate-200 rounded-lg skeleton-shimmer mb-3"></div>
              <div className="h-4 w-20 bg-slate-200/70 rounded-full skeleton-shimmer"></div>
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Chart Skeleton */}
          <div className="lg:col-span-2 bg-white/60 backdrop-blur-sm border border-slate-200/40 rounded-xl lg:rounded-2xl p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="h-7 w-48 bg-slate-200 rounded-lg skeleton-shimmer mb-2"></div>
                <div className="h-3 w-32 bg-slate-200/70 rounded-full skeleton-shimmer"></div>
              </div>
              <div className="h-8 w-28 bg-slate-200 rounded-full skeleton-shimmer"></div>
            </div>
            <div className="h-[280px] lg:h-[320px] bg-slate-200/50 rounded-xl skeleton-shimmer"></div>
          </div>

          {/* Quick Stats + Insights Skeleton */}
          <div className="space-y-6">
            <div className="bg-white/60 backdrop-blur-sm border border-slate-200/40 rounded-2xl p-8">
              <div className="h-7 w-32 bg-slate-200 rounded-lg skeleton-shimmer mb-6"></div>
              <div className="space-y-4">
                {Array(4).fill(0).map((_, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-slate-100/50 last:border-b-0">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-slate-200 rounded-full skeleton-shimmer"></div>
                      <div className="h-5 w-24 bg-slate-200 rounded skeleton-shimmer"></div>
                    </div>
                    <div className="h-7 w-16 bg-slate-200 rounded-lg skeleton-shimmer"></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-white/80 via-emerald-50/30 to-blue-50/30 backdrop-blur-sm border border-slate-200/30 rounded-2xl p-6">
              <div className="h-7 w-28 bg-slate-200 rounded-lg skeleton-shimmer mb-4"></div>
              <div className="space-y-3">
                {Array(3).fill(0).map((_, i) => (
                  <div key={i} className="h-20 bg-white/50 border border-emerald-200/30 rounded-xl skeleton-shimmer"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gradient-to-br from-slate-50 to-blue-50/30 min-h-full" data-testid="dashboard-page" style={{ fontFamily: 'Georgia, serif' }}>

      {/* Header */}
      <div className="mb-10">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent">
          Dashboard
        </h1>
        <p className="text-slate-500 mt-2 text-lg">Screening pipeline overview</p>
      </div>

      {/* Personalization Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2 mb-6 sm:mb-8 bg-white/90 backdrop-blur rounded-xl p-4 border border-slate-200/50 shadow-sm hover:bg-white focus-within:bg-white transition-all duration-200">
        <span className="text-sm font-medium text-slate-600">Showing:</span>
        <select
          value={viewMode}
          onChange={e => setViewMode(e.target.value)}
          data-testid="dashboard-view-selector"
          className="bg-slate-50 hover:bg-white focus:bg-white border border-slate-200/50 hover:border-blue-300/50 focus:border-blue-400/60 focus:shadow-md rounded-lg px-3 py-2 text-sm font-medium outline-none transition-all duration-200"
        >
          <option value="personal">My Pipeline</option>
          <option value="team">Team</option>
          <option value="workspace">Workspace</option>
        </select>
        <div className="ml-auto text-xs text-slate-400 font-medium">
          {viewMode === 'personal' ? '(12 items)' : viewMode === 'team' ? '(42 items)' : '(156 items)'}
        </div>
      </div>

      {/* Empty state 1: No jobs */}
      {overview !== null && (overview?.total_jobs || 0) === 0 && (
        <div className="text-center py-20 px-8 bg-gradient-to-br from-blue-50 to-emerald-50/50 rounded-3xl border-2 border-dashed border-blue-200 mb-10" data-testid="empty-no-jobs">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-emerald-400 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <Briefcase className="w-12 h-12 text-white drop-shadow-lg" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mb-3">No Jobs Yet</h2>
          <p className="text-slate-600 text-lg mb-8 max-w-md mx-auto leading-relaxed">
            Create your first job posting to unlock AI screening and candidate matching.
          </p>
          <button
            onClick={() => navigate('/jobs')}
            className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-3xl hover:scale-[1.02] transition-all duration-300 inline-flex items-center gap-3"
          >
            <Plus className="w-5 h-5" /> Create First Job
          </button>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 lg:mb-10">
        {statCards.map((card) => (
          <div
            key={card.label}
            onClick={() => navigate(card.route)}
            data-testid={`stat-card-${card.label.toLowerCase().replace(/ /g, '-')}`}
            className="group bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-7 hover:shadow-2xl hover:border-blue-400/60 hover:-translate-y-2 hover:bg-white transition-all duration-300 cursor-pointer active:scale-[0.98]"
          >
            <div className="flex items-center justify-between mb-5">
              <div className={`w-12 h-12 rounded-xl border ${card.color} flex items-center justify-center group-hover:scale-110 transition-all duration-300`}>
                <card.icon className="w-6 h-6" />
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" />
            </div>
            <p className="text-3xl font-black text-slate-900 mb-1">{card.value}</p>
            <p className="text-sm font-medium text-slate-600 mb-3">{card.label}</p>
            <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full font-semibold shadow-sm">
              <TrendingUp className="w-3 h-3" /> {card.trend}
            </span>
          </div>
        ))}
      </div>

      {/* Empty state 2: Jobs exist but no candidates */}
      {overview !== null && (overview?.total_candidates || 0) === 0 && (overview?.total_jobs || 0) > 0 && (
        <div className="text-center py-20 px-8 bg-gradient-to-br from-emerald-50 to-blue-50/50 rounded-3xl border-2 border-dashed border-emerald-200 mb-10" data-testid="empty-no-candidates">
          <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-blue-400 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <Users className="w-12 h-12 text-white drop-shadow-lg" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mb-3">No Candidates Yet</h2>
          <p className="text-slate-600 text-lg mb-8 max-w-md mx-auto leading-relaxed">
            Upload resumes or connect LinkedIn sourcing to populate your pipeline.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/candidates')} className="bg-gradient-to-r from-emerald-500 to-blue-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all">Upload Resumes</button>
            <button onClick={() => navigate('/candidates')} className="border-2 border-slate-200 bg-white text-slate-800 px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:bg-slate-50 transition-all">Connect LinkedIn</button>
          </div>
        </div>
      )}

      {/* Charts + Side Panel Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-8 lg:mb-12">

        {/* Screenings Chart */}
        <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-xl lg:rounded-2xl p-6 lg:p-8 shadow-lg lg:shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Screenings (Last 14 Days)</h3>
              <p className="text-sm text-slate-500">Total vs Completed</p>
            </div>
            <div className="flex items-center gap-2 text-xs bg-slate-100/80 px-3 py-1.5 rounded-full border font-medium">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              87% completion rate
            </div>
          </div>
          {callsByDay.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[280px] lg:h-[320px] text-center" data-testid="empty-no-screenings">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-400 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
                <Phone className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">No Screenings Yet</h3>
              <p className="text-slate-500 mb-8 max-w-sm mx-auto">Run your first AI-powered screening call to see activity trends here.</p>
              <button
                onClick={() => navigate('/candidates')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
              >
                Start First Screening
              </button>
            </div>
          ) : (
            <>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={callsByDay} barGap={2} barCategoryGap={8}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: '#94a3b8', fontSize: 12, fontFamily: 'Georgia, serif' }}
                tickFormatter={d => d.slice(5)}
                axisLine={false} tickLine={false}
              />
              <YAxis
                tick={{ fill: '#94a3b8', fontSize: 12, fontFamily: 'Georgia, serif' }}
                axisLine={false} tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
                  fontFamily: 'Georgia, serif', boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                }}
              />
              <Bar dataKey="total" fill="#bfdbfe" radius={[6, 6, 0, 0]} name="Total" />
              <Bar dataKey="completed" fill="#2563eb" radius={[6, 6, 0, 0]} name="Completed" />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-[#bfdbfe]" />
              <span className="text-xs text-slate-500 font-medium">Total</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-[#2563eb]" />
              <span className="text-xs text-slate-500 font-medium">Completed</span>
            </div>
          </div>
            </>
          )}
        </div>

        {/* Right column: Quick Stats + AI Insights */}
        <div className="lg:col-span-1 space-y-6">

          {/* Quick Stats */}
          <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-lg">
            <h3 className="text-base font-bold text-slate-900 mb-4">Quick Stats</h3>
            <div className="space-y-3">
              {[
                { label: 'Shortlisted',  value: overview?.shortlisted_candidates || 24, dot: 'bg-emerald-500', color: 'emerald-500' },
                { label: 'Avg Score',    value: overview?.avg_resume_score ? `${overview.avg_resume_score}/10` : '8.2/10', dot: 'bg-blue-500', color: 'blue-500' },
                { label: 'Conversion',   value: '23%',                                                                      dot: 'bg-amber-500', color: 'amber-500' },
                { label: 'Team Members', value: overview?.team_members || 8,                                                dot: 'bg-violet-500', color: 'violet-500' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-b-0">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${item.dot}`} />
                    <span className="text-sm text-slate-600">{item.label}</span>
                  </div>
                  <span className={`text-sm font-bold ${quickStatColor[item.color]}`}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* AI Insights Panel */}
          <div className="bg-gradient-to-br from-white/90 via-emerald-50/50 to-blue-50/50 backdrop-blur-sm border border-slate-200/30 rounded-2xl p-6 shadow-2xl shadow-emerald-500/10" data-testid="ai-insights-panel">
            <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-500" />
              AI Insights
            </h3>
            <p className="text-xs text-slate-500 mb-4">Smart recommendations for your pipeline</p>
            <div className="space-y-3">
              {aiInsights.length === 0 ? (
                <div className="text-center py-12 px-6" data-testid="empty-no-insights">
                  <Sparkles className="w-16 h-16 text-slate-400 mx-auto mb-4 opacity-50" />
                  <h4 className="text-lg font-semibold text-slate-700 mb-2">No Insights Yet</h4>
                  <p className="text-slate-500 text-sm">Complete more screenings to unlock AI recommendations.</p>
                </div>
              ) : aiInsights.map((insight) => (
                <div
                  key={insight.id}
                  onClick={() => navigate(insight.route)}
                  data-testid={`ai-insight-${insight.id}`}
                  className="group bg-white/70 hover:bg-white border border-emerald-200/40 rounded-lg lg:rounded-xl p-3 lg:p-4 hover:shadow-lg hover:-translate-y-0.5 sm:hover:-translate-y-1 transition-all duration-300 cursor-pointer hover:border-emerald-300/60"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-r ${insight.bg} flex items-center justify-center shadow-lg flex-shrink-0`}>
                      <insight.icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <h4 className="text-sm font-bold text-slate-900 group-hover:text-emerald-700 transition-colors leading-tight">{insight.title}</h4>
                        {insight.count && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">
                            {insight.count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mb-2 leading-snug">{insight.subtitle}</p>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 bg-gradient-to-r ${insight.bg} text-white px-3 py-1 rounded-lg text-[11px] font-bold shadow`}>
                          <insight.icon className="w-2.5 h-2.5" /> {insight.action}
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all ml-auto" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
